/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react';
import { useState } from 'react';
import { useField } from 'formik';
import { useRelayEnvironment, fetchQuery } from 'react-relay';
import PropTypes from 'prop-types';

// Local Definitions

import "./styles.scss";
import { Combobox, MultipleCombobox } from "./common";

/**
 * This is the component description.
 */
const QueryCombobox = ({
  id,
  style,
  className,
  query,
  multiple,
  name,
  label,
  labelColor,
  getTagColor,
  color,
  optional,
  description,
  disabled,
  debounceMs,
  searchVariable,
  extraVariables,
  optionsKeyMap,
  minLength,
  transformSearch,
  validate,
  placeholder,
  promptCreateNewOption
  // ...otherProps
}) => {
  const [, meta, helpers] = useField({
    name,
    validate
  });
  const {
    setValue,
    setError
  } = helpers;
  const {
    value
  } = meta;
  const environment = useRelayEnvironment();
  const [isFetching, setIsFetching] = useState(false);
  const [options, setOptions] = useState([]);
  const {
    graphql,
    accessor
  } = query;
  const getOptions = inputValue => {
    if (isFetching) return;
    let variables = {
      [searchVariable]: inputValue
    };
    if (extraVariables) {
      variables = {
        ...variables,
        ...extraVariables
      };
    }
    fetchQuery(environment, graphql, variables).subscribe({
      start: () => {
        setIsFetching(true);
      },
      complete: () => {
        setIsFetching(false);
      },
      error: fetchError => {
        setIsFetching(false);
        if (setError) setError(fetchError.message);
      },
      next: data => {
        setOptions(data[accessor].edges.map(({
          node
        }) => ({
          value: node[optionsKeyMap.value],
          label: optionsKeyMap.getLabel(node)
        })));
      }
    });
  };
  const comboboxProps = {
    id,
    style,
    options,
    getOptions,
    setOptions,
    debounceMs,
    disabled,
    name,
    label,
    labelColor,
    getTagColor,
    optional,
    description,
    setValue,
    value,
    color,
    isFetching,
    className,
    minLength,
    transformSearch,
    placeholder,
    promptCreateNewOption
  };
  const Input = multiple ? MultipleCombobox : Combobox;
  return /*#__PURE__*/React.createElement(Input, comboboxProps);
};
QueryCombobox.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string),
  /**
   * The name of the custom select input
   */
  name: PropTypes.string,
  /**
   * The label of the custom select input
   */
  label: PropTypes.string,
  /**
   * The label color of the custom select input
   */
  labelColor: PropTypes.string,
  /**
   * The custom select input description
   */
  description: PropTypes.string,
  /**
   * Whether the input should be disabled
   */
  disabled: PropTypes.bool,
  /**
   * The base color of the tiles
   */
  color: PropTypes.string,
  /**
   * The debounce time in milliseconds
   */
  debounceMs: PropTypes.number,
  /**
   * The graphql query to fetch the options and the accessor to destructure the results from
   */
  query: PropTypes.shape({
    accessor: PropTypes.string,
    graphql: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired
  }),
  /**
   *  The extra variables required to be used in the query.
   */
  extraVariables: PropTypes.objectOf(PropTypes.string),
  /**
   *  The select option keys to be used to map the data to the select options.
   * i.e `{ value: 'id', label: 'name' }`
   */
  optionsKeyMap: PropTypes.shape({
    value: PropTypes.string.isRequired,
    getLabel: PropTypes.func.isRequired
  }),
  /**
   * Whether to allow multiple items selection
   */
  multiple: PropTypes.bool,
  /**
   * The variable to be used to search the data
   */
  searchVariable: PropTypes.string,
  /**
   * The minimum length of the search input to start fetching the options
   */
  minLength: PropTypes.number,
  /**
   * The function to transform the search input
   */
  transformSearch: PropTypes.func,
  /**
   * The query combobox field validator function
   */
  validate: PropTypes.func,
  /*
  * Whether the input is optional or not
  */
  optional: PropTypes.bool,
  /**
   * Whether to prompt the user to create a new option if the search input
   * does not match any of the options
   */
  promptCreateNewOption: PropTypes.bool,
  /**
   * The placeholder text for the input
   * */
  placeholder: PropTypes.string,
  /**
   * The function to get the color of the tag
   * */
  getTagColor: PropTypes.func
};
QueryCombobox.defaultProps = {
  optionsKeyMap: {
    value: 'id',
    getLabel: node => node.name
  },
  multiple: false,
  color: 'interactive',
  searchVariable: 'search',
  transformSearch: search => search,
  minLength: 2,
  disabled: false,
  promptCreateNewOption: false
};
export default QueryCombobox;